/**
 * Pure row-selection / refund-summary logic for the sale receipt renderer.
 *
 * Isolated from the canvas code so it is unit-testable (the bitmap pipeline has
 * no test harness). The v4 backend rewrites the legacy `status` column as
 * refunds settle — the parent payment goes SUCCESS → PARTIALLY_REFUNDED →
 * REFUNDED, and settled refund child rows are REFUNDED (never SUCCESS) — so
 * "which rows are payments" cannot be a plain SUCCESS filter.
 */
import currency from 'currency.js';
import {
  PaymentStatusV4,
  SalesChannelEnum,
  ActionStatusEnum,
  SourceStatusEnum,
  PaymentTransactionTypeV4,
  FinancialPaymentTransactionType,
} from '@am3nd-org/pointcraft-types';

type Txn = FinancialPaymentTransactionType;

export interface SaleReceiptChannelBand {
  channelLabel: string;
  orderLabel: string | null;
}

/** Channel band shown between the customer receipt's header rules. */
export function getSaleReceiptChannelBand(
  channel?: SalesChannelEnum,
  orderNumber?: string | null
): SaleReceiptChannelBand {
  let channelLabel: string;
  switch (channel) {
    case SalesChannelEnum.WEB_PAYMENT:
    case SalesChannelEnum.PAYMENT_LINK:
      channelLabel = 'ONLINE';
      break;
    case SalesChannelEnum.INVOICE:
      channelLabel = 'INVOICE';
      break;
    case SalesChannelEnum.MOBILE_SERVICE:
      channelLabel = 'MOBILE SERVICE';
      break;
    case SalesChannelEnum.POS:
    default:
      channelLabel = 'IN STORE';
      break;
  }

  const normalizedOrderNumber = orderNumber?.trim();
  return {
    channelLabel,
    orderLabel: normalizedOrderNumber ? `Order: ${normalizedOrderNumber}` : null,
  };
}

/** Legacy statuses meaning money was captured on a payment row (refunded or not). */
const CAPTURED_STATUSES: readonly string[] = [
  ActionStatusEnum.SUCCESS,
  ActionStatusEnum.PARTIALLY_REFUNDED,
  ActionStatusEnum.REFUNDED,
];

/**
 * Payment rows whose money was captured — the lines printed under the total.
 * Refund child rows are excluded (they print in the refund section instead);
 * refunded parents stay so the receipt still shows how the sale was paid.
 */
export function selectCapturedPayments(transactions: Txn[]): Txn[] {
  return transactions.filter(
    (t) =>
      t.transactionType !== PaymentTransactionTypeV4.REFUND &&
      CAPTURED_STATUSES.includes(t.status)
  );
}

/** Settled v4 refund child rows. In-flight and failed refunds never print. */
export function selectSettledRefunds(transactions: Txn[]): Txn[] {
  return transactions.filter(
    (t) =>
      t.transactionType === PaymentTransactionTypeV4.REFUND &&
      (t.statusV4 === PaymentStatusV4.SUCCEEDED || t.status === ActionStatusEnum.REFUNDED)
  );
}

/** The payment a refund was issued against — refund rows carry no card info of their own. */
export function findRefundParent(refund: Txn, transactions: Txn[]): Txn | undefined {
  if (!refund.parentTransactionId) return undefined;
  return transactions.find((t) => t.id === refund.parentTransactionId);
}

export interface SaleRefundSummary {
  /** Settled principal refunded across all payments (dollars). */
  refundedTotal: number;
  /** Grand total minus refunds, floored at zero (dollars). */
  netTotal: number;
  /** Centered banner text; null when the sale has no settled refunds. */
  bannerLabel: 'REFUNDED' | 'PARTIALLY REFUNDED' | null;
}

/**
 * Refund figures for the receipt, summed from the parents' cumulative
 * `refundedAmount` — the backend syncs that from settled refunds only, and it
 * is also the only signal legacy v2 refunds have (they never created child
 * rows). The sale status picks the banner; when it disagrees with the amounts
 * (e.g. a legacy row on a PAID sale) the amounts decide.
 */
export function summarizeRefunds(args: {
  transactions: Txn[];
  saleStatus?: string;
  grandTotal: number;
}): SaleRefundSummary {
  const { transactions, saleStatus, grandTotal } = args;

  const refundedTotal = selectCapturedPayments(transactions).reduce(
    (sum, t) => sum.add(t.refundedAmount ?? 0),
    currency(0)
  ).value;

  if (refundedTotal <= 0) {
    return { refundedTotal: 0, netTotal: grandTotal, bannerLabel: null };
  }

  let bannerLabel: SaleRefundSummary['bannerLabel'];
  if (saleStatus === SourceStatusEnum.REFUNDED) {
    bannerLabel = 'REFUNDED';
  } else if (saleStatus === SourceStatusEnum.PARTIALLY_REFUNDED) {
    bannerLabel = 'PARTIALLY REFUNDED';
  } else {
    bannerLabel = refundedTotal >= grandTotal ? 'REFUNDED' : 'PARTIALLY REFUNDED';
  }

  const netTotal = Math.max(0, currency(grandTotal).subtract(refundedTotal).value);
  return { refundedTotal, netTotal, bannerLabel };
}
